import * as React from 'react';
import type { FieldErrors, FieldPath, UseFormGetValues } from 'react-hook-form';
import { type FieldValues, type UseControllerProps, type FieldError, useFormState } from 'react-hook-form';

import { isErrorType } from 'af-components/RHFTableCellComponents/error.types';
import type { CellError } from 'af-components/RHFTableCellComponents/error.types';

type RHFUnregisteredFieldWrapperProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
	render: (params: {
		value: ReturnType<UseFormGetValues<TFieldValues>>;
		error: Nullable<CellError>;
	}) => React.ReactNode;
	getValues: UseFormGetValues<TFieldValues>;
};

/**
 * Field Wrapper around components that are only for display (neeed in tables with lots of data)
 * Offers non controlled component, using react hook form methods to retrieve value and errors
 */
function RHFUnregisteredFieldWrapper<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
>(props: RHFUnregisteredFieldWrapperProps<TFieldValues, TName, TTransformedValues>) {

	const {
		control,
		name,
		getValues,
		render,
	} = props;

	const value = React.useMemo(() => getValues(name), [getValues, name]);

	const { errors } = useFormState({ control, name });

	const parsedError: Nullable<CellError> = React.useMemo(() => {
		const fieldError = getErrorFromPath(errors, name) as FieldError | undefined;
		if (fieldError?.message) {
			return {
				message: fieldError.message,
				type: isErrorType(fieldError.type) ? fieldError.type : 'other',
			};
		}
		return null;
	}, [errors, name]);

	return (
		<>
			{render({ value, error: parsedError })}
		</>
	);
}

export default React.memo(RHFUnregisteredFieldWrapper) as typeof RHFUnregisteredFieldWrapper;

export function getErrorFromPath<T extends FieldValues>(
	errors: FieldErrors<T>,
	name: string
): Nullable<FieldError> {
	const parts = name.split('.');
	let result: unknown = errors;

	for (const part of parts) {
		const key = /^\d+$/.test(part) ? Number(part) : part;
		if (result && typeof result === 'object' && key in result) {
			result = (result as Record<string | number, unknown>)[key];
		} else {
			return null;
		}
	}

	return (result as FieldError) ?? null;
}
